import { Avatar } from '@/ui/Avatars/Avatar';
import { DiamondIcon } from '@/ui/Icons/DiamondIcon';
import type { IconProps } from '@/ui/Icons/IconProps';
import { LightningIcon } from '@/ui/Icons/LightningIcon';

import { classNames } from '@/utils/classnames';
import { DiscoveryPreset } from '@/utils/discovery/types';

export type DiscoveryCategoriesProps = {
  activeCategory: string | null;
  onChange: (id: DiscoveryPreset) => void;
  includePopular: boolean;
};

type DiscoveryCategory = {
  id: DiscoveryPreset;
  label: string;
  Icon?: React.FC<IconProps>;
  iconSrc?: string;
};

const baseIconSrc = '/images/tokens/';
const DISCOVERY_CATEGORIES: DiscoveryCategory[] = [
  { id: 'popular', label: 'Popular', Icon: DiamondIcon },
  { id: 'trending', label: 'Trending', Icon: LightningIcon },
  { id: 'currencies', label: 'Currencies', iconSrc: `${baseIconSrc}usdc.webp` },
  { id: 'wrapped', label: 'Wrapped', iconSrc: `${baseIconSrc}wbtc.webp` },
  { id: 'stocks', label: 'Stocks', iconSrc: `${baseIconSrc}nvdax.webp` },
  { id: 'commodities', label: 'Commodities', iconSrc: `${baseIconSrc}gold.webp` },
  { id: 'social', label: 'Social', iconSrc: `${baseIconSrc}zora.webp` },
  { id: 'launchpads', label: 'Launchpads', iconSrc: `${baseIconSrc}pump.webp` },
  { id: 'lsts', label: 'LSTs', iconSrc: `${baseIconSrc}steth.webp` },
] as const;

export const DiscoveryCategories = ({
  activeCategory,
  onChange,
  includePopular,
}: DiscoveryCategoriesProps) => (
  <div className="flex flex-row overflow-auto no-scrollbar p-1 -m-1">
    {DISCOVERY_CATEGORIES.filter(({ id }) => includePopular || id !== 'popular').map(
      ({ id, label, Icon, iconSrc }) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={classNames(
            'flex flex-col shrink-0 gap-4 items-center tablet:w-28 w-21.5 px-2 pt-3 rounded-[16px] transition-all',
            'hover:bg-token-alphalight-200  focus-visible:outline-[3px] focus-visible:outline-token-alphalight-500',
            activeCategory === id ? 'text-token-gray-1600' : 'text-token-gray-900',
          )}
        >
          {Icon != null && <Icon className="size-6 transition-all" />}
          {iconSrc != null && <Avatar src={iconSrc} size={24} />}
          <p
            className={classNames(
              'border-b-2 transition-all ease-out duration-200 font-body text-[13px] tablet:text-[14px] leading-[16px] pb-4',
              activeCategory === id
                ? 'border-token-gray-1600 font-medium'
                : 'border-transparent font-normal',
            )}
          >
            {label}
          </p>
        </button>
      ),
    )}
  </div>
);
